import type { EmployeeProfile, LearningEvidence } from "../engine"
import { BAUSTEIN_BY_ID } from "./bausteine"
import { deriveEvidence } from "./evidence-seed"

/**
 * The curated demo cast — the foreground of every demo run.
 *
 * SYNTHETIC PEOPLE. Names, CVs, MAG excerpts and learning histories are
 * invented; only the org-unit names are real (they come from the anonymised
 * `public/orgchartdata.json`, so "Rayon 3" and "Kreditcenter Firmenkunden" are
 * units that actually exist).
 *
 * Each cast member exists to make ONE point on stage (plan §3.2). The order
 * below is the order to demo them in.
 *
 * The `skills` holdings carry a `source`, and the demo's Datenquellen toggle
 * filters on it — turning `mag` off must still leave a valid plan, which is the
 * live answer to the works-council objection (plan §6).
 */

const TEAM_LEAD_ID = "cast-vogt"

const RAW_CAST: EmployeeProfile[] = [
    // ========================================================================
    // 1 — THE FLAGSHIP CASE
    // Pflicht (FIDLEG lapsing) + a real gap, at 80% Pensum. Dropping her to
    // 60% on stage is the single most persuasive moment in the demo.
    // ========================================================================
    {
        id: "cast-berger",
        displayName: "Berger Sandra",
        managerId: TEAM_LEAD_ID,
        orgUnit: "50041201",
        orgUnitLabel: "Rayon 3",
        kurzzeichen: "SBE",
        roleId: "kundenberater",
        roleLabel: "Kundenberater:in Privatkunden",
        pensum: 80,
        yearsInField: 7,
        aspiration: "anlagespezialist",
        skills: [
            { skillId: "fach.beratungsprozess", level: 3, source: "stellenprofil" },
            { skillId: "fach.gespraechsfuehrung", level: 3, source: "stellenprofil" },
            { skillId: "fach.produktkenntnis", level: 3, source: "stellenprofil" },
            { skillId: "fach.vorsorge", level: 2, source: "cv", evidence: "Vorsorgeberatung seit 2021" },
            { skillId: "fach.hypotheken", level: 2, source: "lernhistorie" },
            { skillId: "fach.anlageberatung", level: 2, source: "lernhistorie" },
            {
                skillId: "fach.esgBeratung",
                level: 0,
                source: "mag",
                evidence:
                    "Kundinnen und Kunden fragen zunehmend nach nachhaltigen Anlagen; hier fühle ich mich noch unsicher.",
            },
            { skillId: "dig.digitaleKanaele", level: 2, source: "stellenprofil" },
        ],
        completed: [
            { bausteinId: "beratungsprozess", completedOn: "2019-04-15" },
            { bausteinId: "gespraechsfuehrung", completedOn: "2020-09-10" },
            { bausteinId: "vorsorge-basis", completedOn: "2021-03-22" },
            { bausteinId: "anlageberatung-basis", completedOn: "2021-11-08" },
            { bausteinId: "hypotheken-basis", completedOn: "2022-06-14" },
            { bausteinId: "fidleg-basis", completedOn: "2019-02-01" },
            // Lapses within the 60-day lead time at asOf 2026-09-01 → FIDLEG
            // becomes due on stage without touching any lever.
            { bausteinId: "fidleg-refresher", completedOn: "2025-10-05" },
            { bausteinId: "gwg-basis", completedOn: "2019-02-20" },
            { bausteinId: "gwg-refresher", completedOn: "2026-02-11" },
            { bausteinId: "vsb-kyc", completedOn: "2024-05-30" },
            { bausteinId: "datenschutz", completedOn: "2025-06-18" },
            { bausteinId: "infosec-basis", completedOn: "2026-01-20" },
            { bausteinId: "ai-risiko", completedOn: "2025-03-12" },
        ],
        artefacts: {
            cv: `Sandra Berger, geboren 1991, wohnhaft in Winterthur.

Ausbildung
2007–2010 Kaufmännische Grundbildung Bank, Zürcher Kantonalbank
2013 Bankfachfrau mit eidg. Fachausweis
2018 CAS Finanzberatung, Kalaidos FH

Berufserfahrung
2010–2016 Sachbearbeiterin Schalter und Zahlungsverkehr, Zürcher Kantonalbank
2016–2019 Kundenberaterin Privatkunden, Bank Cler, Winterthur
seit 2019 Kundenberaterin Privatkunden, Acme Bank, Rayon 3 (80%)

Schwerpunkte: Vorsorge- und Finanzierungsberatung für Privatkunden im
Segment bis USD 500'000 Vermögen. Betreuung eines Portfolios von rund
340 Kundenbeziehungen. Mitarbeit im Projekt "Beratungsdossier digital"
(2023) als Testerin aus dem Vertrieb.

Sprachen: Deutsch (Muttersprache), Englisch (B2), Französisch (A2)`,
            mag: `Mitarbeitergespräch 2026 — Auszug

Leistungsbeurteilung: Sandra erfüllt die Erwartungen durchgehend und
übertrifft sie in der Kundenbindung. Die Rückmeldungen aus dem Rayon sind
sehr gut, die Abschlussquote liegt über dem Teamdurchschnitt.

Entwicklungsthemen aus dem Gespräch:
Sandra bringt selbst ein, dass Kundinnen und Kunden zunehmend nach
nachhaltigen Anlagelösungen fragen und sie sich bei der Erhebung der
Nachhaltigkeitspräferenzen sowie bei kritischen Rückfragen zu ESG-Produkten
noch unsicher fühlt. Sie möchte hier gezielt aufbauen.

Zweites Thema: Bei komplexeren Vorsorgefällen (Einkauf Pensionskasse,
Pensionierungsplanung) zieht sie heute regelmässig den Finanzplaner bei.
Sie wünscht sich, mehr davon selbst abdecken zu können.

Vereinbarung: Weiterbildungsschwerpunkt 2026/27 im Bereich Nachhaltigkeit.
Pensum bleibt bei 80%.`,
        },
    },

    // ========================================================================
    // 2 — LEVEL AWARENESS
    // 15 years in credit. Must NOT be offered "Kreditanalyse: Grundlagen".
    // Carries Kreditkompetenz A, so an obligation binds via signing authority
    // rather than via role — a distinction SAP's skill matching cannot make.
    // ========================================================================
    {
        id: "cast-kuebler",
        displayName: "Kübler Thomas",
        managerId: null,
        orgUnit: "50037740",
        orgUnitLabel: "Kreditcenter Firmenkunden",
        kurzzeichen: "TKU",
        roleId: "kreditanalyst",
        roleLabel: "Kreditanalyst:in",
        pensum: 100,
        yearsInField: 15,
        signingAuthority: "Kreditkompetenz A",
        aspiration: "datenanalyse",
        skills: [
            { skillId: "fach.kreditanalyse", level: 4, source: "stellenprofil" },
            { skillId: "fach.bilanzanalyse", level: 4, source: "cv", evidence: "15 Jahre Kreditanalyse KMU" },
            { skillId: "fach.kreditrisiko", level: 3, source: "stellenprofil" },
            { skillId: "fach.tragbarkeit", level: 4, source: "stellenprofil" },
            { skillId: "fach.immobilienbewertung", level: 3, source: "lernhistorie" },
            { skillId: "dig.excel", level: 3, source: "selbsteinschaetzung" },
            {
                skillId: "reg.opRisk",
                level: 1,
                source: "mag",
                evidence: "Möchte die Portfoliosicht auf das Kreditbuch besser verstehen.",
            },
        ],
        completed: [
            { bausteinId: "kreditanalyse-basis", completedOn: "2011-09-05" },
            { bausteinId: "bilanzanalyse", completedOn: "2013-03-18" },
            { bausteinId: "hypotheken-basis", completedOn: "2012-05-14" },
            { bausteinId: "tragbarkeit", completedOn: "2014-08-25" },
            { bausteinId: "immobilienbewertung", completedOn: "2017-02-13" },
            { bausteinId: "gwg-basis", completedOn: "2011-10-10" },
            { bausteinId: "gwg-refresher", completedOn: "2026-03-04" },
            { bausteinId: "datenschutz", completedOn: "2025-09-15" },
            { bausteinId: "infosec-basis", completedOn: "2025-11-24" },
            { bausteinId: "ai-risiko", completedOn: "2024-08-19" },
        ],
        artefacts: {
            cv: `Thomas Kübler, geboren 1983, wohnhaft in Baden.

Ausbildung
1999–2002 Kaufmännische Grundbildung Bank
2006 Bankfachmann mit eidg. Fachausweis
2011 Dipl. Kreditfachmann, ABS

Berufserfahrung
2002–2008 Sachbearbeiter Kredite, Raiffeisen
2008–2011 Kreditanalyst Privatkunden, Acme Bank
seit 2011 Kreditanalyst Firmenkunden, Acme Bank, Kreditcenter

Kreditkompetenz A seit 2018. Beurteilt jährlich rund 400 Kreditanträge
im KMU-Segment. Fachverantwortlicher für Bauabrechnungen und
Generalunternehmer-Finanzierungen. Ausbildner für neue Analystinnen und
Analysten im Kreditcenter.`,
            mag: `Mitarbeitergespräch 2026 — Auszug

Leistungsbeurteilung: Thomas ist der fachliche Anker im Kreditcenter. Die
Qualität seiner Anträge ist ausgezeichnet, die interne Revision hatte in den
letzten drei Prüfungen keine Feststellungen zu seinen Dossiers.

Entwicklungsthemen aus dem Gespräch:
Thomas möchte die Einzelfallsicht ergänzen um eine Portfoliosicht — welche
Klumpenrisiken das Kreditbuch trägt und wie Frühwarnindikatoren im Bestand
gelesen werden. Er sieht das als Voraussetzung, um künftig im
Kreditkomitee substanzieller mitreden zu können.

Hinweis Führungskraft: Bitte keine Grundlagenkurse mehr zuweisen — das war
2025 zweimal der Fall und hat zu Recht für Unmut gesorgt.

Vereinbarung: Fokus auf Risikoportfolio-Themen.`,
        },
    },

    // ========================================================================
    // 3 — THE CAREER STEP
    // New Teamleiterin: leadership requirements her previous role never had,
    // plus a one-off Arbeitsrecht obligation triggered by the promotion.
    // Also the manager for the manager view.
    // ========================================================================
    {
        id: TEAM_LEAD_ID,
        displayName: "Vogt Andrea",
        managerId: null,
        orgUnit: "50041201",
        orgUnitLabel: "Rayon 3",
        kurzzeichen: "AVO",
        roleId: "teamleiter",
        roleLabel: "Teamleiter:in",
        pensum: 100,
        yearsInField: 11,
        aspiration: "none",
        skills: [
            { skillId: "fach.beratungsprozess", level: 4, source: "cv" },
            { skillId: "fach.gespraechsfuehrung", level: 4, source: "cv" },
            { skillId: "fach.produktkenntnis", level: 3, source: "stellenprofil" },
            {
                skillId: "fue.feedback",
                level: 1,
                source: "mag",
                evidence:
                    "Erste Feedbackgespräche als Führungskraft empfand ich als schwierig, besonders bei kritischen Rückmeldungen.",
            },
            { skillId: "fue.delegation", level: 1, source: "selbsteinschaetzung" },
        ],
        completed: [
            { bausteinId: "beratungsprozess", completedOn: "2015-05-11" },
            { bausteinId: "gespraechsfuehrung", completedOn: "2016-10-03" },
            { bausteinId: "anlageberatung-basis", completedOn: "2017-04-20" },
            { bausteinId: "vorsorge-basis", completedOn: "2018-09-17" },
            { bausteinId: "fidleg-basis", completedOn: "2019-01-28" },
            { bausteinId: "gwg-basis", completedOn: "2015-06-08" },
            { bausteinId: "datenschutz", completedOn: "2025-04-09" },
            { bausteinId: "infosec-basis", completedOn: "2026-02-16" },
            { bausteinId: "ai-risiko", completedOn: "2025-07-22" },
        ],
        artefacts: {
            cv: `Andrea Vogt, geboren 1987, wohnhaft in Zürich.

Ausbildung
2003–2006 Kaufmännische Grundbildung Bank
2011 Bankfachfrau mit eidg. Fachausweis
2019 CAS Leadership, HWZ

Berufserfahrung
2006–2015 Kundenberaterin, diverse Institute
2015–2026 Kundenberaterin Privatkunden, Acme Bank, Rayon 3
seit 01.04.2026 Teamleiterin Rayon 3 (7 Mitarbeitende)

Langjährige Beraterin mit sehr guter Kundenbindung. Seit April 2026 in der
ersten Führungsfunktion.`,
            mag: `Mitarbeitergespräch 2026 — Auszug

Andrea hat die Teamleitung Rayon 3 im April 2026 übernommen. Der Einstieg
ist fachlich reibungslos verlaufen; das Team akzeptiert sie.

Entwicklungsthemen aus dem Gespräch:
Andrea beschreibt den Rollenwechsel als die grösste Umstellung. Erste
Feedbackgespräche empfand sie als schwierig, insbesondere bei kritischen
Rückmeldungen an ehemalige Kolleginnen und Kollegen. Sie neigt dazu,
Aufgaben selbst zu erledigen statt zu delegieren.

Vereinbarung: Führungsausbildung im ersten Jahr priorisieren. Andrea ist
sich bewusst, dass daneben die MAG-Runde 2027 ansteht, die sie erstmals
selbst führen wird.`,
        },
    },

    // ========================================================================
    // 4 — THE HARD PRIORITISATION
    // Returner at 60%: three lapsed certifications and the smallest budget in
    // the cast. Every developmental item must yield; not one Pflicht may.
    // ========================================================================
    {
        id: "cast-frei",
        displayName: "Frei Nadine",
        managerId: TEAM_LEAD_ID,
        orgUnit: "50041201",
        orgUnitLabel: "Rayon 3",
        kurzzeichen: "NFR",
        roleId: "kundenberater",
        roleLabel: "Kundenberater:in Privatkunden",
        pensum: 60,
        yearsInField: 9,
        aspiration: "nachhaltigkeit",
        skills: [
            { skillId: "fach.beratungsprozess", level: 3, source: "stellenprofil" },
            { skillId: "fach.gespraechsfuehrung", level: 3, source: "stellenprofil" },
            { skillId: "fach.produktkenntnis", level: 2, source: "lernhistorie" },
            { skillId: "fach.vorsorge", level: 2, source: "lernhistorie" },
            {
                skillId: "dig.digitaleKanaele",
                level: 0,
                source: "mag",
                evidence:
                    "Während der Abwesenheit wurde die Beratungsstrecke umgestellt; hier muss ich aufholen.",
            },
        ],
        completed: [
            { bausteinId: "beratungsprozess", completedOn: "2017-08-21" },
            { bausteinId: "gespraechsfuehrung", completedOn: "2018-11-12" },
            { bausteinId: "vorsorge-basis", completedOn: "2019-05-06" },
            { bausteinId: "fidleg-basis", completedOn: "2020-02-17" },
            // Everything below lapsed during the leave — the whole point of her.
            { bausteinId: "fidleg-refresher", completedOn: "2024-03-11" },
            { bausteinId: "gwg-basis", completedOn: "2017-09-04" },
            { bausteinId: "gwg-refresher", completedOn: "2024-04-08" },
            { bausteinId: "datenschutz", completedOn: "2023-06-19" },
            { bausteinId: "infosec-basis", completedOn: "2024-05-27" },
            { bausteinId: "ai-risiko", completedOn: "2024-02-05" },
        ],
        artefacts: {
            cv: `Nadine Frei, geboren 1989, wohnhaft in Uster.

Ausbildung
2005–2008 Kaufmännische Grundbildung Bank
2014 Bankfachfrau mit eidg. Fachausweis

Berufserfahrung
2008–2015 Sachbearbeiterin und Beraterin, Acme Bank
2015–2024 Kundenberaterin Privatkunden, Acme Bank, Rayon 3 (100%)
2024–2026 Mutterschaftsurlaub und unbezahlter Urlaub
seit 03/2026 Rückkehr als Kundenberaterin Privatkunden (60%)`,
            mag: `Mitarbeitergespräch 2026 — Auszug (Rückkehrgespräch)

Nadine ist im März 2026 nach 22 Monaten Abwesenheit mit einem Pensum von
60% zurückgekehrt. Fachlich ist sie schnell wieder angekommen, die
Kundenrückmeldungen sind unverändert gut.

Themen aus dem Gespräch:
Während der Abwesenheit wurde die digitale Beratungsstrecke umgestellt und
mehrere Weisungen wurden revidiert. Nadine benennt das selbst als grösste
Lücke. Zudem sind mehrere Pflichtschulungen abgelaufen.

Nadine weist darauf hin, dass ihr Zeitbudget bei 60% begrenzt ist und sie
Weiterbildung nur in Blöcken einplanen kann.

Vereinbarung: Pflichtschulungen zuerst, danach Aufholen der digitalen
Themen. Keine zusätzliche Belastung im ersten Halbjahr.`,
        },
    },

    // ========================================================================
    // 5 — THE UNREGULATED ROLE
    // Pure development plan (only the bank-wide obligations bind). Proves the
    // engine is not just a compliance tracker.
    // ========================================================================
    {
        id: "cast-raman",
        displayName: "Raman Priya",
        managerId: null,
        orgUnit: "50058047",
        orgUnitLabel: "IT Risk & BCM",
        kurzzeichen: "PRA",
        roleId: "it-engineer",
        roleLabel: "IT Engineer",
        pensum: 100,
        yearsInField: 5,
        aspiration: "projektleitung",
        skills: [
            { skillId: "dig.softwareEngineering", level: 3, source: "stellenprofil" },
            { skillId: "dig.cloudGrundlagen", level: 2, source: "lernhistorie" },
            { skillId: "dig.aiGrundlagen", level: 2, source: "lernhistorie" },
            {
                skillId: "dig.securityEngineering",
                level: 1,
                source: "mag",
                evidence:
                    "Im Review-Prozess fehlt mir Sicherheit bei Threat Modelling und Secrets Management.",
            },
            { skillId: "met.agile", level: 2, source: "selbsteinschaetzung" },
            {
                skillId: "met.praesentation",
                level: 1,
                source: "mag",
                evidence: "Möchte Resultate vor dem Architekturboard souveräner vertreten.",
            },
        ],
        completed: [
            { bausteinId: "cloud-grundlagen", completedOn: "2023-04-17" },
            { bausteinId: "agile", completedOn: "2022-09-26" },
            { bausteinId: "ai-grundlagen", completedOn: "2025-05-13" },
            { bausteinId: "ai-risiko", completedOn: "2025-05-20" },
            { bausteinId: "datenschutz", completedOn: "2025-08-11" },
            { bausteinId: "infosec-basis", completedOn: "2026-03-02" },
        ],
        artefacts: {
            cv: `Priya Raman, geboren 1995, wohnhaft in Zürich.

Ausbildung
2014–2018 BSc Informatik, ZHAW
2021 Azure Solutions Architect Associate

Berufserfahrung
2018–2021 Software Engineer, Swisscom
seit 2021 Software Engineer, Acme Bank, IT Risk & BCM

Entwicklung und Betrieb interner Risiko-Reportinganwendungen. Technische
Ansprechperson für die BCM-Toolchain. Seit 2025 Mitarbeit im
Architekturboard als Gast.`,
            mag: `Mitarbeitergespräch 2026 — Auszug

Leistungsbeurteilung: Priya liefert technisch konstant hohe Qualität und
ist im Team die gefragte Ansprechperson für die BCM-Toolchain.

Entwicklungsthemen aus dem Gespräch:
Priya möchte mittelfristig Richtung Projektleitung. Zwei Themen hat sie
selbst benannt: Erstens fehlt ihr im Code-Review-Prozess Sicherheit bei
Threat Modelling und Secrets Management. Zweitens möchte sie Resultate vor
dem Architekturboard souveräner vertreten — heute überlässt sie das meist
dem Teamleiter.

Vereinbarung: Karriereziel Projektleitung wird unterstützt. Priya bleibt
2026/27 fachlich verankert und baut parallel Methodenkompetenz auf.`,
        },
    },

    // ========================================================================
    // 6 — THE APPRENTICE
    // Long horizon, broad structured curriculum, almost no history.
    // ========================================================================
    {
        id: "cast-bianchi",
        displayName: "Bianchi Luca",
        managerId: TEAM_LEAD_ID,
        orgUnit: "50041201",
        orgUnitLabel: "Rayon 3",
        kurzzeichen: "LBI",
        roleId: "lernender",
        roleLabel: "Lernende:r / Nachwuchs",
        pensum: 100,
        yearsInField: 1,
        aspiration: "none",
        skills: [
            { skillId: "fach.produktkenntnis", level: 1, source: "stellenprofil" },
            { skillId: "met.selbstorganisation", level: 1, source: "mag", evidence: "Priorisierung fällt noch schwer." },
        ],
        completed: [{ bausteinId: "infosec-basis", completedOn: "2025-08-18" }],
        artefacts: {
            cv: `Luca Bianchi, geboren 2007, wohnhaft in Dietikon.

Ausbildung
seit 08/2025 Kaufmännische Grundbildung Bank (EFZ), Acme Bank

Bisherige Einsätze
08/2025–02/2026 Kundendienst und Zahlungsverkehr
seit 03/2026 Rayon 3, Beratungsunterstützung`,
            mag: `Standortgespräch 2026 — Auszug

Luca ist im ersten Lehrjahr und im März 2026 in den Beratungsbereich
gewechselt. Er arbeitet zuverlässig und ist im Team gut aufgenommen.

Themen aus dem Gespräch:
Priorisierung und Selbstorganisation fallen ihm noch schwer, besonders wenn
mehrere Aufträge gleichzeitig anfallen. Fachlich fehlen ihm erwartungsgemäss
noch die Produkt- und Prozesskenntnisse.

Vereinbarung: Strukturierter Aufbau über die verbleibende Lehrzeit.`,
        },
    },

    // ========================================================================
    // 7–10 — the rest of Andrea's team + two specialists, so the manager view
    // has a plausible spread and the L&D view a few named faces.
    // ========================================================================
    {
        id: "cast-rossi",
        displayName: "Rossi Marco",
        managerId: TEAM_LEAD_ID,
        orgUnit: "50041201",
        orgUnitLabel: "Rayon 3",
        kurzzeichen: "MRO",
        roleId: "kundenberater",
        roleLabel: "Kundenberater:in Privatkunden",
        pensum: 100,
        yearsInField: 3,
        aspiration: "finanzplanung",
        skills: [
            { skillId: "fach.beratungsprozess", level: 2, source: "stellenprofil" },
            { skillId: "fach.gespraechsfuehrung", level: 2, source: "lernhistorie" },
            { skillId: "fach.produktkenntnis", level: 2, source: "stellenprofil" },
            {
                skillId: "fach.vorsorge",
                level: 1,
                source: "mag",
                evidence: "Vorsorgefälle gebe ich heute fast immer ab.",
            },
        ],
        completed: [
            { bausteinId: "beratungsprozess", completedOn: "2023-06-12" },
            { bausteinId: "gespraechsfuehrung", completedOn: "2024-02-19" },
            { bausteinId: "fidleg-basis", completedOn: "2023-05-08" },
            { bausteinId: "fidleg-refresher", completedOn: "2026-01-14" },
            { bausteinId: "gwg-basis", completedOn: "2023-05-22" },
            { bausteinId: "gwg-refresher", completedOn: "2026-01-28" },
            { bausteinId: "datenschutz", completedOn: "2025-10-06" },
            { bausteinId: "infosec-basis", completedOn: "2026-04-13" },
            { bausteinId: "ai-risiko", completedOn: "2025-09-01" },
        ],
        artefacts: {
            mag: `Mitarbeitergespräch 2026 — Auszug

Marco ist seit drei Jahren in der Beratung und entwickelt sich gut. Er
benennt selbst, dass er Vorsorgefälle heute fast immer an die Finanzplanung
abgibt, und möchte diesen Bereich selbst abdecken können.

Vereinbarung: Entwicklungsziel Richtung Finanzplanung wird unterstützt.`,
        },
    },
    {
        id: "cast-dubois",
        displayName: "Dubois Céline",
        managerId: TEAM_LEAD_ID,
        orgUnit: "50041199",
        orgUnitLabel: "Team Anlegen",
        kurzzeichen: "CDU",
        roleId: "anlageberater",
        roleLabel: "Anlageberater:in",
        pensum: 90,
        yearsInField: 8,
        aspiration: "compliance",
        skills: [
            { skillId: "fach.anlageberatung", level: 3, source: "stellenprofil" },
            { skillId: "fach.wertschriften", level: 3, source: "stellenprofil" },
            { skillId: "fach.portfolio", level: 2, source: "lernhistorie" },
            { skillId: "fach.gespraechsfuehrung", level: 3, source: "cv" },
            {
                skillId: "fach.esgBeratung",
                level: 1,
                source: "mag",
                evidence: "Nachhaltigkeitspräferenzen erhebe ich heute eher pflichtgemäss als beratend.",
            },
        ],
        completed: [
            { bausteinId: "anlageberatung-basis", completedOn: "2019-03-14" },
            { bausteinId: "portfoliokonstruktion", completedOn: "2021-10-21" },
            { bausteinId: "gespraechsfuehrung", completedOn: "2020-05-19" },
            { bausteinId: "beratungsprozess", completedOn: "2018-09-11" },
            { bausteinId: "esg-grundlagen", completedOn: "2024-11-07" },
            { bausteinId: "fidleg-basis", completedOn: "2019-02-04" },
            { bausteinId: "fidleg-refresher", completedOn: "2025-12-02" },
            { bausteinId: "gwg-basis", completedOn: "2018-10-16" },
            { bausteinId: "gwg-refresher", completedOn: "2025-11-18" },
            { bausteinId: "datenschutz", completedOn: "2024-09-24" },
            { bausteinId: "infosec-basis", completedOn: "2025-10-29" },
            { bausteinId: "ai-risiko", completedOn: "2024-06-11" },
        ],
    },
    {
        id: "cast-steiner",
        displayName: "Steiner Michael",
        managerId: null,
        orgUnit: "50037740",
        orgUnitLabel: "Kreditcenter Firmenkunden",
        kurzzeichen: "MST",
        roleId: "firmenkundenberater",
        roleLabel: "Firmenkundenberater:in",
        pensum: 100,
        yearsInField: 12,
        signingAuthority: "Kreditkompetenz B",
        aspiration: "kreditspezialist",
        skills: [
            { skillId: "fach.firmenkunden", level: 3, source: "stellenprofil" },
            { skillId: "fach.kreditanalyse", level: 2, source: "lernhistorie" },
            { skillId: "fach.bilanzanalyse", level: 2, source: "cv" },
            { skillId: "fach.zahlungsverkehr", level: 2, source: "stellenprofil" },
            { skillId: "met.verhandlung", level: 3, source: "cv" },
            {
                skillId: "reg.sanktionen",
                level: 0,
                source: "mag",
                evidence: "Bei Sanktionstreffern bin ich unsicher, wann eskaliert werden muss.",
            },
        ],
        completed: [
            { bausteinId: "kreditanalyse-basis", completedOn: "2016-04-26" },
            { bausteinId: "firmenkunden", completedOn: "2017-11-09" },
            { bausteinId: "verhandlung", completedOn: "2019-06-04" },
            { bausteinId: "zahlungsverkehr", completedOn: "2018-02-20" },
            { bausteinId: "gwg-basis", completedOn: "2014-08-12" },
            { bausteinId: "gwg-refresher", completedOn: "2025-08-26" },
            { bausteinId: "vsb-kyc", completedOn: "2023-03-15" },
            { bausteinId: "datenschutz", completedOn: "2025-05-07" },
            { bausteinId: "infosec-basis", completedOn: "2025-12-16" },
            { bausteinId: "ai-risiko", completedOn: "2024-10-22" },
            { bausteinId: "fidleg-basis", completedOn: "2019-03-19" },
            { bausteinId: "fidleg-refresher", completedOn: "2025-09-30" },
        ],
    },
    {
        id: "cast-lehmann",
        displayName: "Lehmann Reto",
        managerId: null,
        orgUnit: "50055310",
        orgUnitLabel: "Support Systems",
        kurzzeichen: "RLE",
        roleId: "projektleiter",
        roleLabel: "Projektleiter:in",
        pensum: 80,
        yearsInField: 14,
        aspiration: "teamleitung",
        skills: [
            { skillId: "met.projektmanagement", level: 3, source: "stellenprofil" },
            { skillId: "met.agile", level: 3, source: "lernhistorie" },
            { skillId: "met.praesentation", level: 3, source: "cv" },
            { skillId: "met.selbstorganisation", level: 3, source: "selbsteinschaetzung" },
            {
                skillId: "fue.feedback",
                level: 0,
                source: "mag",
                evidence: "Fachliche Führung ohne Weisungsbefugnis funktioniert; echte Führung habe ich nie gelernt.",
            },
        ],
        completed: [
            { bausteinId: "projektmanagement", completedOn: "2014-05-20" },
            { bausteinId: "agile", completedOn: "2019-09-10" },
            { bausteinId: "praesentation", completedOn: "2016-03-08" },
            { bausteinId: "selbstorganisation", completedOn: "2021-01-26" },
            { bausteinId: "datenschutz", completedOn: "2025-02-11" },
            { bausteinId: "infosec-basis", completedOn: "2026-05-19" },
            { bausteinId: "ai-risiko", completedOn: "2025-04-15" },
        ],
    },
]

/**
 * Authored evidence for the moments the demo turns on.
 *
 * Everything else is derived deterministically from the same hash the
 * population uses, so the cast is not a special case of the engine — only of
 * the narration. These three exist because the story has to be exact:
 *
 *  - Sandra passed the FIDLEG refresher's knowledge check with room to spare,
 *    and nothing beyond it. Under Kirkpatrick she is compliant. Under LTEM the
 *    same row stops counting, her duty reopens, and the plan grows — the same
 *    transcript, judged against a different standard. That is the whole
 *    argument in one person.
 *  - Her ESG course has no assessment at all, which is what makes "attended"
 *    and "competent" visibly different on her card.
 *  - Nadine's data-protection transfer was confirmed by her manager with three
 *    of the four required drivers in place, so the manager view opens on a row
 *    that is already answered rather than an empty queue.
 */
const AUTHORED_EVIDENCE: Record<string, LearningEvidence[]> = {
    "cast-berger": [
        {
            bausteinId: "fidleg-refresher",
            reaction: { relevance: 4, likelihoodToApply: 4, on: "2025-10-06" },
            learning: {
                instrument: "wissenstest",
                scaled: 0.84,
                passed: true,
                on: "2025-10-05",
            },
        },
        {
            bausteinId: "anlageberatung-basis",
            reaction: { relevance: 5, likelihoodToApply: 4, on: "2021-11-09" },
        },
    ],
    "cast-frei": [
        {
            bausteinId: "datenschutz",
            reaction: { relevance: 4, likelihoodToApply: 5, on: "2024-11-04" },
            learning: {
                instrument: "entscheidungstest",
                scaled: 0.79,
                passed: true,
                on: "2024-11-03",
            },
            bridge: {
                deliverable: "Löschkonzept für Altbestände",
                meetsCriteria: true,
                on: "2024-11-24",
            },
            behaviour: {
                applied: "yes",
                selfReported: true,
                verifiedBy: "cast-vogt",
                on: "2024-11-27",
                drivers: ["monitor", "reinforce", "encourage"],
            },
        },
    ],
}

export const CAST: EmployeeProfile[] = RAW_CAST.map((employee) => ({
    ...employee,
    evidence: deriveEvidence(employee.id, employee.completed, BAUSTEIN_BY_ID, {
        overrides: AUTHORED_EVIDENCE[employee.id],
    }),
}))

export const CAST_BY_ID: ReadonlyMap<string, EmployeeProfile> = new Map(
    CAST.map((employee) => [employee.id, employee]),
)

/** The employee the demo opens on — the flagship case. */
export const DEFAULT_CAST_ID = "cast-berger"

/** Fixed reference date for the whole demo, matching the seed. */
export const DEMO_ASOF = "2026-09-01"
